import React, { useState, useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Badge
} from 'reactstrap'
import { TitlePage } from 'components/atoms'
import {
  DataTable,
  DataTableStatic
} from 'components/organisms'
import { ProductCollectionCreateForm } from 'domains/product-collection/organisms'
import {
  productCollectionAddItemTableColumns,
  productCollectionCancelItemTableColumns
} from 'domains/product-collection/molecules'
import { useDropzone } from 'react-dropzone'
import LoaderButton from 'components/molecules/loader-button'
import ProductsApiV2 from 'api/v2/admins/products-api-v2'
import ProductCollectionsApiV2 from 'api/v2/admins/product-collections-api-v2'
import { ALERT_TYPES } from 'constants/alert-constants'
import PropTypes from 'prop-types'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const TEMPLATE_LINK               = 'https://drive.google.com/file/d/1pnLodVOKn3BuOwVBsScO3tQcUW9CN7h9/view'
const ProductCollectionCreatePage = ({ pageUtils }) => {

  const [ products, setProducts ]                       = useState([])
  const [ pagination, setPagination ]                   = useState({})
  const [ isLoading, setIsLoading ]                     = useState(false)
  const [ selectedItems, setSelectedItems ]             = useState([])
  const [ file, setFile ]                               = useState(new Blob)
  const [ isUploading, setIsUploading ]                 = useState(false)
  const [ isSubmitting, setIsSubmitting ]               = useState(false)
  const [ isProductTableShown, setIsProductTableShown ] = useState(false)
  const history                                         = useHistory()

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setFile(acceptedFiles[0])
    } else {
      pageUtils.setAlertMsg('Please upload file with .csv, .xls, or .xlsx format.', ALERT_TYPES.DANGER)
    }

  }, [ pageUtils ])

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    disabled : isUploading,
    accept   : 'text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  /**
  * Upload Form Styling Begins
  */
  const baseStyle = useMemo(() => ({
    flex            : 1,
    display         : 'flex',
    flexDirection   : 'column',
    alignItems      : 'center',
    margin          : '20px',
    padding         : '20px',
    borderWidth     : 2,
    borderRadius    : 2,
    borderColor     : '#eeeeee',
    borderStyle     : 'dashed',
    backgroundColor : '#fafafa',
    color           : '#bdbdbd',
    outline         : 'none',
    transition      : 'border .24s ease-in-out'
  }), [])

  const activeStyle = useMemo(() => ({
    borderColor: '#2196f3'
  }), [])

  const acceptStyle = useMemo(() => ({
    borderColor: '#00e676'
  }), [])

  const rejectStyle = useMemo(() => ({
    borderColor: '#ff1744'
  }), [])

  const style = useMemo(() => ({
    ...baseStyle,
    ...(isDragActive ? activeStyle : {}),
    ...(isDragAccept ? acceptStyle : {}),
    ...(isDragReject ? rejectStyle : {})
  }), [
    isDragActive,
    isDragReject,
    isDragAccept,
    baseStyle,
    activeStyle,
    acceptStyle,
    rejectStyle
  ])

  const showUploadedFile = file.size === 0
    ?  'Drag and drop a CSV file here, or click to select files to upload product list'
    : file.name

  const uploadFile = (file) => {
    setIsUploading(true)
    ProductsApiV2.get_from_csv(file).then((response) => {
      setSelectedItems(response.data.products)
      pageUtils.setAlertMsg('CSV has been uploaded succesfully!', ALERT_TYPES.SUCCESS)
      pageUtils.setAlertMsg('Please check items in New Items table belows before Submit!', ALERT_TYPES.WARNING)
    }).catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsUploading(false))
  }

  const handleFetchProducts = async (tableState) => {
    await ProductsApiV2.get({ tableState })
      .then((response) => {
        const data = response.data.products
        const meta = response.data.meta

        setProducts(data)
        setPagination(meta)
      })
      .catch((error) => alert(error))
      .finally( () => setIsLoading(false) )
  }

  const handleSubmitForm = async (values) => {
    setIsSubmitting(true)
    const item_ids = selectedItems.map((item) => item.id)
    await ProductCollectionsApiV2.create({ ...values, items: item_ids })
      .then(() => {
        pageUtils.setAlertMsg('Successfully create Product Collection!', ALERT_TYPES.SUCCESS)
        history.push('/app/products/collections')
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data ))
      .finally(() => setIsSubmitting(false))
  }

  const handleClickAddItem = useCallback((data) => {
    setSelectedItems([ ...selectedItems, data ])
  }, [ selectedItems ])

  const handleClickCancelItem = useCallback((data) => {
    const updateSelectedItems = selectedItems.filter((element) => element.sku !== data.sku)
    setSelectedItems(updateSelectedItems)
  }, [ selectedItems ])

  const addItemColumns = useMemo(() => productCollectionAddItemTableColumns({
    selectedItems,
    handleClickAddItem
  }), [ selectedItems, handleClickAddItem ])

  const cancelItemColumns = useMemo(() => productCollectionCancelItemTableColumns({ handleClickCancelItem }), [ handleClickCancelItem ])

  const hiddenColumns = [
    'name',
    'seller_name',
    'status'
  ]

  const NewItemsTable     = ({ selectedItems }) => {
    if (selectedItems.length === 0) return null

    return (
      <div className='flex-fill'>
        <CardHeader>
          { 'New Items' } <Badge color="primary" >{ selectedItems.length }</Badge>
          <Button
            color="danger"
            className='float-right'
            size='sm'
            onClick={ () => setSelectedItems([]) }
          >
            Reset
          </Button>
        </CardHeader>
        <DataTableStatic
          data={ selectedItems }
          columns={ cancelItemColumns }
          hiddenColumns={ hiddenColumns }
        />
      </div>
    )
  }
  NewItemsTable.propTypes = { selectedItems: PropTypes.array }

  return (
    <>
      <TitlePage mainTitle='Create Product Collection' />
      <Row>
        <Col md={ 4 }>
          <Card>
            <CardHeader>
              { 'Add Items by Uploading CSV' }
            </CardHeader>
            <div { ...getRootProps({ style }) }>
              <input { ...getInputProps() } />
              <p className='font-weight-bold'>
                { showUploadedFile }
              </p>
            </div>
            <CardBody>
              <h5>
                { 'Please follow the given CSV template ' }
                <a
                  className='text-primary'
                  href={ TEMPLATE_LINK }
                  target='_blank' rel='noopener noreferrer'
                >
                  { 'here' }
                </a>
              </h5>
              <LoaderButton
                className='float-right'
                color='primary'
                onClick={ () => uploadFile(file) }
                isLoading={ isUploading }
                disabled={ file.size === 0 }
                text = 'Upload'
              />
            </CardBody>
          </Card>
        </Col>
        <Col md={ 8 }>
          <Card>
            <CardHeader>
              { 'Add Item Manually' }
              <Button
                className='float-right'
                color='primary'
                size='sm'
                onClick={ () => setIsProductTableShown(!isProductTableShown) }
              >
                { 'Add New Item' }
              </Button>
            </CardHeader>
            {
              isProductTableShown ?
                <DataTable
                  data={ products }
                  pagination={ pagination }
                  columns={ addItemColumns }
                  hiddenColumns={ hiddenColumns }
                  onFetchData={ handleFetchProducts }
                  isLoading={ isLoading }
                /> : null
            }
          </Card>
        </Col>
      </Row>
      <Card>
        <CardHeader className='font-weight-bold'>
          { 'Product Collection Create Form' }
        </CardHeader>
        <CardBody>
          <ProductCollectionCreateForm
            selectedItems={ selectedItems }
            NewItemsTable={ NewItemsTable }
            handleSubmitForm={ handleSubmitForm }
            isSubmitting={ isSubmitting }
          />
        </CardBody>
      </Card>

    </>
  )
}

ProductCollectionCreatePage.propTypes    = propTypes
ProductCollectionCreatePage.defaultProps = defaultProps

export default ProductCollectionCreatePage
